/**
 * ADR-118 amendment — ONE-side wiring for `@aegix/auth-*`.
 *
 * The canonical auth seam for `apps/aegix-one-react`. Mirrors AIM's
 * `apps/aegix-aim-react/src/lib/aim-auth.ts` and SMS's
 * `apps/aegix-sms-react/src/lib/sms-auth.ts`:
 *
 *   1. **`oneStorage`** — single `StorageAdapter` instance bridging
 *      auth-core's logical `StorageKeys.*` to the canonical
 *      `aegix.auth.v1.*` namespace, with the mobile pattern split
 *      (access token in `sessionStorage`, refresh + everything else
 *      in `localStorage`) and `legacyKeys` promotion of the
 *      pre-canonical `aegix_one_*` names. Same `BridgedStorageConfig`
 *      shape AIM/SMS use, so behavior + lint surface + auth-core
 *      semantics are identical across all three apps.
 *
 *   2. **`get*Token` / `set*Token` accessors** — the per-app surface
 *      the lint rule `aegix/no-raw-token-storage-access` whitelists
 *      every non-`<AuthProvider>` consumer to read through (socket,
 *      service worker, http-client, etc.). ONE has fewer of these
 *      than AIM/SMS (no http-client raw reads — the adapter routes
 *      everything via `httpStorageFromAdapter`).
 *
 *   3. **`oneRefreshAdapter`** — the boot-time refresh adapter,
 *      pre-bound to the Tiaki refresh endpoint. Backed by the
 *      backend's `JWTTokenStrategy.create_refresh_token`,
 *      which adds `meta.refresh_token` to every allauth response —
 *      so a fresh load with the access token expired in
 *      `sessionStorage` and the refresh token persisted in
 *      `localStorage` can mint a new access via
 *      `/api/v2/api-token-refresh/` instead of bouncing to sign-in.
 *
 * **Conscious tradeoff documented in ADR-118 amendment**: ONE moves
 * from HttpOnly-session reliance toward JS-readable tokens (matches
 * AIM/SMS). The `sessionid` cookie still carries the allauth session
 * for the headless side-flows (`allauth-endpoints.ts`); the JWT path
 * coexists.
 */

import {
  StorageKeys,
  createBridgedStorageAdapter,
  type StorageAdapter,
} from '@aegix/auth-core';
import {
  createAllauthAdapter,
  createSimpleJwtRefreshAdapter,
  type AllauthUser,
} from '@aegix/auth-identity';
import { deviceIdFetch } from './deviceIdHeader';

/**
 * ONE's **pre-canonical** storage key names, kept only as legacy slots
 * `createBridgedStorageAdapter` reads-and-promotes on first access.
 * Writes land on the canonical `aegix.auth.v1.*` namespace; these
 * `aegix_one_*` entries get one read each before they're deleted.
 *
 * Pre-canonical ONE kept all of these on `localStorage`. The canonical
 * access token slot lives in `sessionStorage` (uniform mobile pattern
 * with AIM/SMS); the refresh token + user stay on `localStorage`.
 *
 * Exported as `ONE_LEGACY_STORAGE_KEYS` for parity with AIM/SMS;
 * unlike those apps, ONE's `lib/http-client.ts` routes
 * everything through `oneStorage`, so no external consumer needs
 * these names — the export exists for symmetry + future cleanup
 * hooks.
 */
const LEGACY_KEYS = {
  token: 'aegix_one_token',
  refreshToken: 'aegix_one_refresh_token',
  user: 'aegix_one_user',
} as const;

const TOKEN_LEGACY: readonly string[] = Object.freeze([LEGACY_KEYS.token]);
const REFRESH_LEGACY: readonly string[] = Object.freeze([
  LEGACY_KEYS.refreshToken,
]);
const USER_LEGACY: readonly string[] = Object.freeze([LEGACY_KEYS.user]);
const NO_LEGACY: readonly string[] = Object.freeze([]);

function oneLegacyKeys(libKey: string): readonly string[] {
  if (libKey === StorageKeys.token) return TOKEN_LEGACY;
  if (libKey === StorageKeys.refreshToken) return REFRESH_LEGACY;
  if (libKey === StorageKeys.user) return USER_LEGACY;
  return NO_LEGACY; // permissions/activeOrganization/heartbeat — ONE doesn't use these legacy
}

/**
 * ONE uses the **uniform mobile pattern**:
 * access token in `sessionStorage` (ephemeral, dropped on tab
 * close); refresh token + user in `localStorage` (persistent across
 * sessions). Matches AIM and SMS so behavior + lint surface + boot-
 * refresh semantics are identical across all three apps.
 *
 * The load-bearing companion is the boot-time refresh
 * (`libs/auth-identity` provider reconcile) — when a fresh load
 * finds access empty in `sessionStorage` but refresh present in
 * `localStorage`, the provider mints a new access via
 * `oneRefreshAdapter` before deciding logged-out. Mobile-pattern's
 * missing half.
 *
 * Pre-canonical ONE wrote the access token to `localStorage`, NOT
 * `sessionStorage` — so the legacy spelling (`aegix_one_token`)
 * stays on `localStorage` for the promotion read. The adapter then
 * writes the canonical (`sessionStorage`) and deletes the legacy
 * entry; the end state matches the mobile pattern within one read.
 */
function oneStorageBackend(physical: string): Storage | null {
  if (typeof window === 'undefined') return null;
  if (physical === StorageKeys.token) {
    return typeof window.sessionStorage !== 'undefined'
      ? window.sessionStorage
      : null;
  }
  // Everything else (including the legacy access token at
  // `aegix_one_token`, which pre-canonical ONE kept on localStorage)
  // resolves to localStorage.
  return typeof window.localStorage !== 'undefined'
    ? window.localStorage
    : null;
}

/**
 * Create a storage adapter for ONE. Writes land
 * on the canonical `aegix.auth.v1.*` namespace, with the access
 * token in `sessionStorage` (mobile pattern); existing sessions
 * written by the pre-canonical code path (`aegix_one_*` on
 * localStorage) migrate forward losslessly on first read via the
 * adapter's `legacyKeys` capability — the "no-squash existing
 * sessions" gate.
 *
 * The raw-token / JSON-rest rule, the `__cookie__` scrub, the
 * JSON-quoted self-heal, defensive try/catch, and the
 * memory fallback all live in `createBridgedStorageAdapter`. ONE
 * supplies:
 *   - `backendFor` for the access-token-in-sessionStorage split.
 *   - `legacyKeys` for the `aegix_one_*` → canonical migration.
 */
export function createOneStorageAdapter(): StorageAdapter {
  return createBridgedStorageAdapter({
    backendFor: oneStorageBackend,
    legacyKeys: oneLegacyKeys,
  });
}

/**
 * Process-wide ONE storage adapter: the one adapter both
 * `<AuthProvider storage=…>` (main.tsx) and `@tiaki/http-client`
 * (`lib/http-client.ts` via `httpStorageFromAdapter`) consume —
 * nothing reads `localStorage` raw outside this seam. Non-
 * `<AuthProvider>` consumers (allauth-endpoints, sockets, anything
 * else) read the JWT via the accessors below rather than touching
 * storage directly — enforced by the
 * `aegix/no-raw-token-storage-access` rule.
 */
export const oneStorage = createOneStorageAdapter();

/** Read the current ONE access token (raw JWT string) or null. */
export function getOneAccessToken(): string | null {
  return oneStorage.get<string>(StorageKeys.token);
}

/** Persist a fresh access token (stored raw in sessionStorage). */
export function setOneAccessToken(token: string): void {
  oneStorage.set(StorageKeys.token, token);
}

/** Read the refresh token (raw JWT string, localStorage) or null. */
export function getOneRefreshToken(): string | null {
  return oneStorage.get<string>(StorageKeys.refreshToken);
}

/** Persist the refresh token (stored raw in localStorage). */
export function setOneRefreshToken(token: string): void {
  oneStorage.set(StorageKeys.refreshToken, token);
}

/** Drop the refresh token (e.g. on logout). */
export function clearOneRefreshToken(): void {
  oneStorage.remove(StorageKeys.refreshToken);
}

/**
 * ONE's boot-time refresh adapter.
 *
 * Backed by the backend's `JWTTokenStrategy.create_refresh_token`, which
 * surfaces a SimpleJWT-format refresh JWT as `meta.refresh_token`
 * in every allauth completion response. `createSimpleJwtRefreshAdapter`
 * (libs/auth-identity) posts that JWT to
 * `/api/v2/api-token-refresh/` — the SAME endpoint AIM and SMS use
 * — which mints a new access token via `BaseUser.get_jwt()`. No
 * ONE-specific endpoint required.
 *
 * Wired into `<AuthProvider refreshAdapter=…>` in main.tsx so the
 * boot-time reconcile uses it when the access token has
 * expired.
 */
export const oneRefreshAdapter = createSimpleJwtRefreshAdapter();

/**
 * ONE's login adapter. Lives here (not `main.tsx`) so the REAL wired
 * instance is testable without rendering the app — parity with SMS's
 * `smsLoginAdapter` in `lib/sms-auth.ts`. The adapter is stateless
 * aside from its captured
 * config, so module-scope construction stays cheap; `main.tsx` imports
 * this instance into `<AuthProvider loginAdapter=…>`.
 *
 * The adapter's raw-fetch surfaces are ONE's credential mints — the
 * `/_allauth/*` completion calls (password login, OAuth token exchange,
 * passkey login, signup) whose responses carry `meta.access_token` /
 * `meta.refresh_token` (JWTTokenStrategy) — plus logout and the boot-time
 * session probe. `deviceIdFetch` stamps the persisted push device id
 * (`aegix_one_device_id`, `push/deviceId.ts`) on all of them so the
 * minted session is bound to this browser's device identity;
 * a browser with nothing persisted sends no header, which the backend
 * reads as a legacy, unscoped session.
 *
 * `oneRefreshAdapter` above is deliberately NOT stamped: refresh carries
 * the `device_id` claim forward from the refresh token — the header is
 * never re-read on refresh, and AIM/SMS leave their refresh
 * adapters unstamped for the same reason.
 */
export const oneLoginAdapter = createAllauthAdapter<AllauthUser>({
  baseUrl: window.__ENV?.NX_API_URL ?? 'http://localhost:8000',
  useTokenTransport: window.__ENV?.NX_ALLAUTH_APP_CLIENT_ENABLED === 'true',
  fetchImpl: deviceIdFetch,
});

export { LEGACY_KEYS as ONE_LEGACY_STORAGE_KEYS };
